import {
  Appear,
  Button,
  Buttons,
  Card,
  CardContent,
  Divider,
  Flex,
  Notification,
  Tab,
  Tabs,
  Text,
} from '@bitrise/bitkit';
import React, { FunctionComponent, useContext, useEffect, useState } from 'react';
import { useParams, Redirect, Route, Switch } from 'react-router-dom';
import dataAtIds from '../../../dataAtIds.json';
import { FeatureContext } from '../../Features/useFeatures';
import { trackEvent } from '../../../utils/tracking';
import { AuthContextFirebase } from '../../Authenticate/AuthenticateFirebase';
import { URLStateContext } from '../../URLState/URLState';
import GuideAndroidBitriseStep from './Guides/Android/BitriseStep';
import GuideAndroidGradle from './Guides/Android/Gradle';
import GuideIosBitriseStep from './Guides/iOS/BitriseStep';
import GuideIosCocoaPods from './Guides/iOS/CocoaPods';
import GuideIosSPM from './Guides/iOS/SPM';
import SetupOptions from './SetupOptions';

const GettingStarted: FunctionComponent = () => {
  const { appId } = useParams<{ appId: string }>();
  const { push } = useContext(URLStateContext);
  const { hasFeature } = useContext(FeatureContext);
  const {
    data: { firstSeen },
  } = useContext(AuthContextFirebase);
  const [firstSeenInitial] = useState(firstSeen);

  const handleCompleteSetup = () => {
    trackEvent('Onboarding.Setup.Complete', {});
    push(`/${appId}`);
  };

  useEffect(() => {
    if (!firstSeenInitial && firstSeen) {
      trackEvent('Onboarding.Setup.SDKDetected', {});
    }
  }, [firstSeen, firstSeenInitial]);

  return (
    <Appear animation="FadeSlideUp" data-trace-at-id={dataAtIds.GettingStartedPage} duration="base">
      <Card maxWidth="56.25rem">
        <CardContent backgroundColor="gray-2" padding="x10">
          {hasFeature('enable-setup-platform-switch') && (
            <>
              <Tabs alignChildrenHorizontal="middle">
                <Route path="/:appId/o/getting-started/setup/ios">
                  {({ match }) => (
                    <Tab
                      active={!!match}
                      onClick={() => push(`/${appId}/o/getting-started/setup/ios`)}
                      size="x4"
                      data-trace-at-id={dataAtIds.GettingStartedTabIOS}
                    >
                      iOS
                    </Tab>
                  )}
                </Route>

                <Route path="/:appId/o/getting-started/setup/android">
                  {({ match }) => (
                    <Tab
                      active={!!match}
                      onClick={() => push(`/${appId}/o/getting-started/setup/android`)}
                      size="x4"
                      data-trace-at-id={dataAtIds.GettingStartedTabAndroid}
                    >
                      Android
                    </Tab>
                  )}
                </Route>
              </Tabs>

              <Divider color="gray-3" margin="x10" />
            </>
          )}
          <SetupOptions />
        </CardContent>

        <CardContent padding="x10">
          <Switch>
            <Route path="/:appId/o/getting-started/setup/ios">
              <Switch>
                <Route
                  component={GuideIosCocoaPods}
                  path="/:appId/o/getting-started/setup/ios/cocoa"
                />
                <Route component={GuideIosSPM} path="/:appId/o/getting-started/setup/ios/spm" />
                <Route
                  component={GuideIosBitriseStep}
                  path="/:appId/o/getting-started/setup/ios/bitrise-step"
                />
                <Redirect to={`/${appId}/o/getting-started/setup/ios/bitrise-step`} />
              </Switch>
            </Route>
            <Route path="/:appId/o/getting-started/setup/android">
              <Switch>
                <Route
                  component={GuideAndroidBitriseStep}
                  path="/:appId/o/getting-started/setup/android/bitrise-step"
                />
                <Route
                  component={GuideAndroidGradle}
                  path="/:appId/o/getting-started/setup/android/gradle"
                />
                <Redirect to={`/${appId}/o/getting-started/setup/android/bitrise-step`} />
              </Switch>
            </Route>
            <Redirect to={`/${appId}/o/getting-started/setup/ios`} />
          </Switch>

          <Flex textColor="grape-5">
            <Flex alignChildren="middle" direction="horizontal" margin="x8">
              <Flex>
                <Notification type={firstSeen ? 'success' : 'progress'}>
                  {firstSeen ? 'SDK Detected' : 'Scanning for SDK'}
                </Notification>
              </Flex>
            </Flex>

            {firstSeen && (
              <>
                <Text align="middle" config="5">
                  Great, we&apos;ve detected your SDK.
                </Text>
                <Text align="middle" config="7">
                  You&apos;re now ready to start exploring Trace.
                </Text>
              </>
            )}
          </Flex>

          <Buttons alignChildren="middle" paddingVertical="x8">
            <Button
              data-trace-at-id={dataAtIds.GettingStartedSDKData}
              disabled={!firstSeen}
              level="primary"
              onClick={() => handleCompleteSetup()}
            >
              Take me to Trace
            </Button>
          </Buttons>
        </CardContent>
      </Card>
    </Appear>
  );
};

export default GettingStarted;
